import { useEffect } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const highlights = [
  { icon: "ri-film-line", title: "三大内容分类", desc: "电影、电视剧、综艺节目聚合展示，覆盖动作、科幻、悬疑、古装、真人秀等数十种类型。" },
  { icon: "ri-filter-3-line", title: "多条件筛选", desc: "支持类型、地区、年份、标签等条件联动筛选，多选组合，快速缩小查找范围。" },
  { icon: "ri-search-eye-line", title: "关键词搜索", desc: "影视名称与主演关键词模糊匹配，配合排序与筛选，找片更高效。" },
  { icon: "ri-heart-3-line", title: "本地收藏夹", desc: "一键收藏喜欢的影视，收藏状态保存在浏览器本地，无需注册账号即可随时查看。" },
];

const disclaimerPoints = [
  "本站仅作为影视信息的聚合、分类与导航平台，为用户提供内容资料与查询服务。",
  "本站不提供任何视频文件的上传、存储、下载与在线播放服务，播放界面仅为功能演示。",
  "本站不提供任何付费观看、会员充值或下载服务，不涉及任何交易行为。",
  "站内展示的影视资料、海报与简介均为示例数据，仅用于产品功能演示与界面展示。",
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "关于我们 - 91看片｜优质影视资源在线观看平台";
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-4 overflow-hidden rounded-lg bg-primary-950 p-6 md:p-10">
          <span className="inline-block rounded-md bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
            91看片
          </span>
          <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
            优质影视资源在线观看平台
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-100/80 md:text-base">
            91看片致力于为喜爱电影、电视剧和综艺节目的用户，提供丰富、高清、多样化的影视内容分类与便利的筛选查找功能。
            我们希望通过清晰的分类结构、灵活的多条件筛选与高效的搜索能力，帮助你快速发现想看的内容，提升观影选择与信息获取的效率。
          </p>
        </section>

        <section className="mt-8 md:mt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">平台能力</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 md:mt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <a href="#disclaimer">免责声明</a>
          </h2>
          <div id="disclaimer" className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <p className="text-sm leading-relaxed text-foreground-700">
              请在使用本站前仔细阅读以下说明：
            </p>
            <ul className="mt-4 space-y-3">
              {disclaimerPoints.map((point) => (
                <li key={point} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                  <i className="ri-shield-check-line mt-0.5 shrink-0 text-accent-600" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-8 md:mt-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你对内容分类、筛选功能有建议，或发现资料有误，欢迎通过下方表单反馈，我们会认真阅读每一条消息。
              </p>
              <ul className="mt-5 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span className="text-sm text-foreground-700">feedback@91kanpian.demo</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <span className="text-sm text-foreground-700">工作日 09:00 - 18:00</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className="ri-map-pin-line text-lg" />
                  </span>
                  <span className="text-sm text-foreground-700">线上服务平台（演示站点）</span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-3">
              <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">意见反馈</h2>
              <FeedbackForm />
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}